import { useNavigate } from 'react-router-dom'

import { Alert, Box, Section } from '@factorialco/deprecated-design-system'
import { ImportEmployeesRootPageDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import AuthorizeUser from 'modules/core/components/AuthorizeUser'
import WithMultiSelectState from 'modules/core/components/WithMultiSelectState'
import type { WithMultiSelectStateChildrenProps } from 'modules/core/components/WithMultiSelectState'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect, useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import Importer from 'modules/employees/components/Importer'
import type { ImportedEmployee } from 'modules/employees/lib/ImportedEmployee'
import { useEmployeesImporter } from 'modules/employees/lib/employeesImporterContext'

import ImportTable from './components/ImportTable'

const Content = () => {
  const importer = useEmployeesImporter()
  const { company, access } = useCurrentSession()

  useEffect(() => {
    track(trackingEvents.oldImportEmployees.uploadedFileOldEmployeesImporter, {
      companyId: company.id,
      acessId: access.id,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <WithMultiSelectState items={importer.employees} initialSelection={importer.readyEmployees}>
      {({
        onDeselectAll,
        onSelectAll,
        onToggleSelectedItem,
        items,
        selected,
      }: WithMultiSelectStateChildrenProps<ImportedEmployee>) => (
        <>
          <Section
            illustration="multiple-actions-upload"
            title={i18n.t('import.table.title')}
            description={i18n.t('import.table.description')}
          >
            <Importer importer={importer} openModalOnDrop />
          </Section>
          <Box padding="s64" width="full" height="full">
            {importer.isOverlimited && (
              <Box paddingBottom="s24">
                <Alert color="warning">
                  {i18n.t('overlimit_employees.importer.warning', {
                    limitEmployeesCurrentPlan: importer.maxEmployeesAllowedImport,
                    newEmployees: importer.newEmployees,
                  })}
                </Alert>
              </Box>
            )}
            <ImportTable
              employees={items}
              selected={selected}
              attributes={importer.attributes}
              customFields={importer.customFields}
              onToggleSelectedItem={onToggleSelectedItem}
              onDeselectAll={onDeselectAll}
              onSelectAll={onSelectAll}
            />
          </Box>
        </>
      )}
    </WithMultiSelectState>
  )
}

const ImportEmployeesPage = () => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const importer = useEmployeesImporter()
  const { data } = useQuery(ImportEmployeesRootPageDocument)
  const canManageImports = data?.employees?._policies.exportEmployees.can ?? false

  useUnsafeEffect(() => {
    if (!importer.employees.length) {
      navigate(navigation.employees.list.fullPath())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importer.employees, navigate])

  if (!importer.employees.length) return null

  return (
    <AuthorizeUser authorizeFn={() => canManageImports}>
      <Content />
    </AuthorizeUser>
  )
}

export default observer(ImportEmployeesPage)
